import React from 'react';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { styled, useTheme } from '@mui/material/styles';
import { IconClock, IconUsers } from '@tabler/icons-react';
import useMediaQuery from '@mui/material/useMediaQuery';

const ACTIVE_BG = '#001A47';    
const ACTIVE_FG = '#FFFFFF';    
const INACTIVE_FG = '#121A22';  
const DIVIDER = '#D5D8DE';      
const BORDER = '#71767A';       
const HOVER_BG = '#D8E6FF';

const PillGroup = styled(ToggleButtonGroup, {
  shouldForwardProp: (prop) => !['pillHeight', 'pillWidth','fullWidth'].includes(prop),
})(({ pillHeight = 44, pillWidth, marginLeft, fullWidth = false }) => ({
  borderRadius: '16px 16px 16px 16px',
  overflow: 'hidden',
  background: '#FFFFFF',
  height: pillHeight,
  marginLeft: 0,
  width: fullWidth ? '100%' : pillWidth || 'auto',
  display: 'inline-flex',
  flexWrap: 'nowrap',
  '& .MuiToggleButton-root': {
    height: '100%',
  },
}));

const PillButton = styled(ToggleButton, {
  shouldForwardProp: (prop) => !['pillHeight', 'fontSize', 'padX', 'nowrap'].includes(prop),
})(({ fontSize = 14, padX = 16, nowrap = false }) => ({
  textTransform: 'none',
  padding: `0 ${padX}px`,
  fontWeight: 400,
  fontFamily: 'Noto Sans',
  fontSize,
  letterSpacing: 0,
  color: INACTIVE_FG,
  background: '#FFFFFF',
  border: 'none',
  borderRadius: 0,
  flex: 1,
  // Layout for icon + label
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 6, 
  lineHeight: '21px', 
  ...(nowrap
    ? {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'clip',
      }
    : {}),
 
  '& svg': {
    fontSize: fontSize + 2,
    color: 'currentColor',
  },
  '&&&.MuiToggleButtonGroup-grouped': {
    margin: 0,
    border: 'none',
    borderTop: `1px solid ${BORDER}`,
    borderBottom: `1px solid ${BORDER}`,
  },
  '&&&.MuiToggleButtonGroup-grouped:first-of-type': {
    borderLeft: `1px solid ${BORDER}`,
    borderRadius: '16px 0 0 16px',
  },
  '&&&.MuiToggleButtonGroup-grouped:last-of-type': {
    borderRight: `1px solid ${BORDER}`,
    borderRadius: '0 16px 16px 0',
  },
  // Internal divider between two pills
  '&&&.MuiToggleButtonGroup-grouped:not(:first-of-type)': {
    borderLeft: `1px solid ${DIVIDER}`,
  },
  '&&&.MuiToggleButtonGroup-grouped.Mui-selected': {
    background: ACTIVE_BG,
    color: ACTIVE_FG,
    fontWeight: 600,
    boxShadow: 'none',
    outline: 'none',
    borderTop: `1px solid ${ACTIVE_BG}`,
    borderBottom: `1px solid ${ACTIVE_BG}`,
  },
  '&&&.MuiToggleButtonGroup-grouped.Mui-selected:first-of-type': {
    borderLeft: `1px solid ${ACTIVE_BG}`,
    borderRadius: '16px 0 0 16px',
  },
  '&&&.MuiToggleButtonGroup-grouped.Mui-selected:last-of-type': {
    borderRight: `1px solid ${ACTIVE_BG}`,
    borderRadius: '0 16px 16px 0',
  },
  '&&&.MuiToggleButtonGroup-grouped.Mui-selected:not(:first-of-type)': {
    borderLeft: 'none',
  },
  '&&&.MuiToggleButtonGroup-grouped.Mui-selected + .MuiToggleButton-root': {
    // borderLeft was 'none', resulting in a rendering continuity issue at the shared corner. 
    // Adding a divider to create a continuous border around the inactive tab in its default state.
    borderLeft: `1px solid ${DIVIDER}`,
  },
  '&:hover': {
    background: HOVER_BG,
  },
  '&.Mui-selected:hover': {
    background: ACTIVE_BG,
  },
  '&.Mui-focusVisible, &:focus, &:focus-visible': {
    outline: 'none',
    boxShadow: 'none',
  },
}));

export default function SegmentedTabs({
  value,
  onChange,
  groupProps = {},
  height = 44,
  width,         
  fullWidth = false,
  marginLeft,
}) {
  const handleChange = (_, newValue) => {
    if (newValue !== null) onChange(newValue);
  };

  const fontSize = 14;
  const padX = 16;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const iconSize = isMobile ? 24 : 16;

  return (
    <PillGroup
      pillHeight={height}
      pillWidth={isMobile ? undefined : width}
      marginLeft={marginLeft}
      fullWidth={fullWidth}
      value={value}
      exclusive
      onChange={handleChange}
      aria-label="Recently viewed, needs attention and saved tabs"
      {...groupProps}
    >
      <PillButton
        pillHeight={height}
        fontSize={fontSize}
        padX={padX}
        value="recently_viewed"
        nowrap
        aria-label="Recently Viewed"
        sx={{
          flexShrink: 0,
          minWidth: isMobile ? 'auto' : 166,
        }}
      >
        <IconClock size={iconSize} />
        {!isMobile && <span>Recently Viewed</span>}
      </PillButton>

      <PillButton
        pillHeight={height}
        fontSize={fontSize}
        padX={padX}
        value="saved"
        aria-label="Saved by Your Team"
        nowrap
        sx={{
          flexShrink: 0,  
          minWidth: isMobile ? 'auto' : 234,
        }}
      >
        <IconUsers size={iconSize} />
        {!isMobile && <span>Saved by Your Teammates</span>}
      </PillButton>
    </PillGroup>
  );
}
